// pages/secure/ExpensesPage.js
// Operational expense register: add/edit/delete expenses in pop-up windows.
// Each one posts to the ledger automatically, so the P&L stays current.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import Modal from '../../components/secure/Modal';
import DataList from '../../components/shared/DataList';
import { ProtectedLink } from '../../utils/openProtected';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const EMPTY = { expense_date: new Date().toISOString().slice(0, 10), vendor: '', account_id: '', amount: '', payment_method: 'check', reference: '', memo: '', is_recurring: false, receipt: null };

const ExpensesPage = () => {
    const apiClient = useApiClient();
    const [expenses, setExpenses] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [error, setError] = useState('');
    const [form, setForm] = useState(null);       // add or edit
    const [isSaving, setIsSaving] = useState(false);

    const load = useCallback(() => {
        Promise.all([apiClient.get('/api/expenses'), apiClient.get('/api/accounting/accounts')])
            .then(([ex, acc]) => { setExpenses(ex); setAccounts(acc.filter(a => a.type === 'expense')); })
            .catch(err => setError(err.message));
    }, [apiClient]);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!form.account_id || !form.amount) { setError('Account and amount are required.'); return; }
        setIsSaving(true); setError('');
        try {
            if (form.expense_id) {
                await apiClient.put(`/api/expenses/${form.expense_id}`, {
                    expense_date: form.expense_date, vendor: form.vendor, account_id: form.account_id,
                    amount: form.amount, payment_method: form.payment_method, reference: form.reference,
                    memo: form.memo, is_recurring: form.is_recurring,
                });
            } else {
                const fd = new FormData();
                ['expense_date', 'vendor', 'account_id', 'amount', 'payment_method', 'reference', 'memo'].forEach(k => fd.append(k, form[k] ?? ''));
                fd.append('is_recurring', form.is_recurring ? 'true' : 'false');
                if (form.receipt) fd.append('receipt', form.receipt);
                await apiClient.post('/api/expenses', fd, true);
            }
            setForm(null);
            load();
        } catch (err) { setError(err.message); }
        finally { setIsSaving(false); }
    };

    const remove = async (ex) => {
        if (!window.confirm(`Delete the ${fmt(ex.amount)} expense to ${ex.vendor || ex.account_name}?`)) return;
        try { await apiClient.delete(`/api/expenses/${ex.expense_id}`); load(); }
        catch (err) { setError(err.message); }
    };

    const total = expenses.reduce((s, e) => s + Number(e.amount), 0);

    return (
        <div className="p-6 max-w-5xl">
            <div className="flex items-center justify-between mb-1">
                <h1 className="text-2xl font-bold text-slate-800">Operational Expenses</h1>
                <button onClick={() => setForm({ ...EMPTY })} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">+ Add expense</button>
            </div>
            <p className="text-slate-500 mb-4">Record what the office spends — rent, utilities, marketing, dues, supplies. Each posts to the ledger automatically.</p>

            {error && <p className="mb-4 text-red-500">{error}</p>}

            <div className="bg-white rounded-lg shadow p-3">
                <DataList rows={expenses} rowKey="expense_id" csvName="operational-expenses" defaultSort={{ key: 'expense_date', dir: 'desc' }}
                    empty="No expenses recorded yet." filters={[{ key: 'account', label: 'Account' }, { key: 'payment_method', label: 'Method' }]}
                    columns={[
                        { key: 'expense_date', label: 'Date', type: 'date', value: (e) => (e.expense_date ? String(e.expense_date).slice(0, 10) : null) },
                        { key: 'vendor', label: 'Vendor', value: (e) => e.vendor || '—', render: (e) => <>{e.vendor || '—'} {e.is_recurring ? <span className="text-xs text-blue-500" title="recurring">↻</span> : ''}</> },
                        { key: 'account', label: 'Account', value: (e) => `${e.account_code || ''} ${e.account_name || ''}`.trim() },
                        { key: 'payment_method', label: 'Method', render: (e) => <span className="capitalize">{e.payment_method}</span> },
                        { key: 'amount', label: 'Amount', type: 'number', render: (e) => <span className="font-medium">{fmt(e.amount)}</span> },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (e) => (
                            <span className="whitespace-nowrap">
                                {e.receipt_path && <ProtectedLink href={`/uploads/${e.receipt_path}`} className="text-blue-600 hover:underline text-xs mr-2">receipt</ProtectedLink>}
                                <button onClick={() => setForm({ ...e, receipt: null })} className="text-slate-500 hover:underline text-xs mr-2">Edit</button>
                                <button onClick={() => remove(e)} className="text-red-500 hover:underline text-xs">×</button>
                            </span>) },
                    ]} />
                {expenses.length > 0 && <p className="mt-2 text-right text-sm font-medium">Total of all expenses: {fmt(total)}</p>}
            </div>

            {/* Add/edit modal */}
            <Modal open={!!form} onClose={() => setForm(null)} title={form?.expense_id ? 'Edit expense' : 'Add expense'}
                   footer={<>
                       <button onClick={() => setForm(null)} className="px-4 py-2 rounded-md border">Cancel</button>
                       <button onClick={save} disabled={isSaving} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">{isSaving ? 'Saving...' : 'Save'}</button>
                   </>}>
                {form && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="block text-sm font-medium text-slate-700">Date</label>
                                <input type="date" value={form.expense_date} onChange={e => setForm(p => ({ ...p, expense_date: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                            <div><label className="block text-sm font-medium text-slate-700">Amount</label>
                                <input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="0.00" /></div>
                        </div>
                        <div><label className="block text-sm font-medium text-slate-700">Expense account</label>
                            <select value={form.account_id} onChange={e => setForm(p => ({ ...p, account_id: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2">
                                <option value="">Select...</option>
                                {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.code} — {a.name}</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="block text-sm font-medium text-slate-700">Vendor</label>
                                <input value={form.vendor} onChange={e => setForm(p => ({ ...p, vendor: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                            <div><label className="block text-sm font-medium text-slate-700">Payment method</label>
                                <select value={form.payment_method} onChange={e => setForm(p => ({ ...p, payment_method: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2">
                                    {['check', 'ach', 'card', 'cash', 'other'].map(m => <option key={m} value={m}>{m}</option>)}
                                </select></div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="block text-sm font-medium text-slate-700">Reference (check/invoice #)</label>
                                <input value={form.reference} onChange={e => setForm(p => ({ ...p, reference: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                            <div className="flex items-end"><label className="flex items-center gap-2 text-sm text-slate-700">
                                <input type="checkbox" checked={!!form.is_recurring} onChange={e => setForm(p => ({ ...p, is_recurring: e.target.checked }))} /> Recurring</label></div>
                        </div>
                        <div><label className="block text-sm font-medium text-slate-700">Memo</label>
                            <input value={form.memo} onChange={e => setForm(p => ({ ...p, memo: e.target.value }))} className="mt-1 w-full border rounded-md px-3 py-2" /></div>
                        {!form.expense_id && (
                            <div><label className="block text-sm font-medium text-slate-700">Receipt (optional, max 10MB)</label>
                                <input type="file" onChange={e => setForm(p => ({ ...p, receipt: e.target.files[0] }))} className="mt-1 w-full" /></div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ExpensesPage;
